// Persistence: session library (PRD §6 — the trace is the source of truth).
// Every exploration is kept in a local library (index + one entry per session)
// so past sessions can be resumed and continued. No backend.

import { storage } from "../lib/storage";
import { newSessionId, state } from "./state";

const LS_INDEX = "lattice.library.v1";
const LS_LEGACY = "lattice.session.v1";
const sessionKey = (id: string) => `lattice.session.${id}`;

export interface LibraryEntry { id: string; idea: string; updatedAt: number; nodeCount: number }

function readIndex(): LibraryEntry[] {
  try { return JSON.parse(storage.get(LS_INDEX) || "") || []; } catch { return []; }
}
function writeIndex(ix: LibraryEntry[]): void {
  storage.set(LS_INDEX, JSON.stringify(ix));
}

export function listSessions(): LibraryEntry[] { return readIndex(); }

export function loadSession(id: string): boolean {
  const raw = storage.get(sessionKey(id));
  if (!raw) return false;
  try {
    if (!hydrate(JSON.parse(raw))) return false;
    state.sessionId = id;
    return true;
  } catch { return false; }
}

export function deleteSession(id: string): void {
  storage.remove(sessionKey(id));
  writeIndex(readIndex().filter((e) => e.id !== id));
}

/** Adopt a pre-library single-session save as the first library entry. */
function migrateLegacy(): void {
  const raw = storage.get(LS_LEGACY);
  if (!raw) return;
  try {
    const s = JSON.parse(raw);
    if (s && s.rootId) {
      const id = newSessionId();
      storage.set(sessionKey(id), raw);
      const ix = readIndex();
      ix.unshift({ id, idea: s.idea || "(untitled)", updatedAt: Date.now(), nodeCount: (s.nodes || []).length });
      writeIndex(ix);
    }
  } catch { /* corrupt legacy — drop it */ }
  storage.remove(LS_LEGACY);
}

export function snapshot(): any {
  return {
    idea: state.idea, rootId: state.rootId, currentId: state.currentId,
    facts: state.facts, grounding: state.grounding, constraints: state.constraints, seq: state.seq,
    sessionId: state.sessionId, reviews: state.reviews, doc: state.doc,
    // prefetch holds transient promises/detached nodes — never persisted.
    nodes: [...state.nodes.values()].map(({ prefetch, ...n }) => n),
  };
}

export function save(): void {
  if (!state.rootId) return;   // nothing worth indexing yet
  storage.set(sessionKey(state.sessionId), JSON.stringify(snapshot()));
  const ix = readIndex().filter((e) => e.id !== state.sessionId);
  ix.unshift({ id: state.sessionId, idea: state.idea, updatedAt: Date.now(), nodeCount: state.nodes.size });
  writeIndex(ix.slice(0, 40));
}

export function hydrate(s: any): boolean {
  if (!s || !s.rootId || !Array.isArray(s.nodes) || !s.nodes.length) return false;
  Object.assign(state, {
    idea: s.idea || "", rootId: s.rootId, currentId: s.currentId || s.rootId,
    facts: s.facts || [], grounding: s.grounding || [], constraints: s.constraints || [], seq: s.seq || 0,
    sessionId: s.sessionId || state.sessionId, reviews: s.reviews || [],
    doc: s.doc && s.doc.secs ? { secs: s.doc.secs, folded: s.doc.folded || [] } : { secs: {}, folded: [] },
    nodes: new Map(s.nodes.map((n: any) => [n.id, { ...n, prefetch: {} }])),
  });
  return true;
}

export function restore(): boolean {
  migrateLegacy();
  const ix = readIndex();
  return ix.length ? loadSession(ix[0]!.id) : false;
}

export function exportSession(): string { return JSON.stringify(snapshot(), null, 2); }
